import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import HotTopics from '@/components/feature/HotTopics';
import { getArticleById, getHotArticles, getRelatedArticles, categories } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = getArticleById(articleId);
  const hotArticles = getHotArticles();
  const categoryInfo = categories.find((c) => c.slug === article?.categorySlug);

  if (!article) {
    return (
      <>
        <title>文章不存在 - 51吃瓜网</title>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-200 rounded-full">
              <i className="ri-file-unknow-line text-foreground-400 w-10 h-10 flex items-center justify-center"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-950 mb-2">文章不存在</h1>
            <p className="text-foreground-500 text-sm mb-6">这篇瓜可能已经被删除了</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.id, article.categorySlug);
  const articleUrl = `${window.location.origin}/article/${article.id}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '51吃瓜网',
      logo: {
        '@type': 'ImageObject',
        url: `${window.location.origin}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  return (
    <>
      <title>{article.title} - 51吃瓜网</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={`51吃瓜网,${article.tags.join(',')},${article.category}`} />
      <link rel="canonical" href={articleUrl} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:image" content={article.imageUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={articleUrl} />
      <meta property="article:published_time" content={article.publishedAt} />
      <meta property="article:author" content={article.author} />
      <meta property="article:tag" content={article.tags.join(',')} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.imageUrl} />

      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <article className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 min-w-0">
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {article.category}
                  </Link>
                  {article.isHot && (
                    <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-fire-line w-3 h-3 flex items-center justify-center"></i>
                      热门
                    </span>
                  )}
                </div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-heading text-foreground-950 leading-snug mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                    {article.viewCount.toLocaleString()} 阅读
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center"></i>
                    {article.commentCount.toLocaleString()} 评论
                  </span>
                </div>
              </div>

              <div className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - 51吃瓜网`}
                  className="w-full h-auto max-h-[500px] object-cover"
                />
              </div>

              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {relatedArticles.length > 0 && (
                <section className="mt-10 pt-8 border-t border-background-200" aria-label="相关推荐">
                  <h3 className="text-lg font-heading text-foreground-950 mb-5">相关推荐</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    {relatedArticles.map((rel) => (
                      <ArticleCard key={rel.id} article={rel} />
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className="lg:w-80 shrink-0">
              <div className="sticky top-24 space-y-6">
                <HotTopics articles={hotArticles} />

                {categoryInfo && (
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                    <h4 className="font-heading text-base text-foreground-950 mb-3">
                      关于{categoryInfo.name}
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      {categoryInfo.description}
                    </p>
                    <Link
                      to={`/category/${categoryInfo.slug}`}
                      className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
                    >
                      查看更多
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </>
  );
}